import { Controller } from 'stimulus';
import Tooltip from 'bootstrap/js/src/tooltip';

const TARGET_SELECTOR = '[data-point-tooltip]';

// One delegating Bootstrap tooltip for every plotted point inside this container.
//
// Deliberately not the `tooltip` controller, which builds one Tooltip per element in
// connect(). This page draws a point per resident per setting per day — a full Resident
// Settings page is around 14,000 of them — and one controller and one Tooltip each would
// be tens of thousands of instances built at first paint. Bootstrap's `selector` option
// is the delegation this needs: the container carries the only listeners, and a child
// Tooltip is built lazily for a point the operator actually hovers.
//
// Delegation is also what survives Turbo: a point rendered into the container later, by
// a frame update or a filter pass, is covered with no re-initialisation.
export default class extends Controller {
  connect() {
    this.tooltip = new Tooltip(this.element, {
      selector: TARGET_SELECTOR,
      trigger: 'hover',
      placement: 'top',
      // A label carries the day's severity as the shared icon (an <i>), so the bubble
      // renders markup. Sanitising stays on: Bootstrap's default allowlist passes `i`
      // and, globally, `class`/`role`/`aria-*`, which is exactly what the icon needs and
      // nothing more. Every label these targets carry is built from an `_html`
      // translation key, so Rails escapes the interpolated date and value first.
      html: true,
      // Body-mounted so a tooltip near a table's edge is not clipped by the cell's
      // overflow, and so it is never a child of a node Turbo is about to replace.
      container: 'body',
      fallbackPlacements: ['top', 'bottom'],
    });

    document.addEventListener('show.bs.modal', this.hideTooltips);
    // Turbo clones the body for its cache before Stimulus disconnects, so a tooltip open
    // at navigation time would be baked into the snapshot and restored on the way back.
    document.addEventListener('turbo:before-cache', this.disposeDelegated);
  }

  disconnect() {
    document.removeEventListener('show.bs.modal', this.hideTooltips);
    document.removeEventListener('turbo:before-cache', this.disposeDelegated);
    this.disposeDelegated();
    this.tooltip.dispose();
    this.tooltip = null;
  }

  // Bootstrap keys a delegated instance off the element in a strong Map, so the ones
  // built during this container's life outlive it unless they are disposed with it.
  disposeDelegated = () => {
    this.element.querySelectorAll(TARGET_SELECTOR).forEach((point) => {
      Tooltip.getInstance(point)?.dispose();
    });
  };

  hideTooltips = () => {
    this.tooltip.hide();
    this.element.querySelectorAll(TARGET_SELECTOR).forEach((point) => {
      Tooltip.getInstance(point)?.hide();
    });
  };
}
